/**
 * @fileoverview Unified FlowGrid SDK facade.
 * @module flowgrid
 *
 * @description
 * A single class that aggregates every feature module so consumers can
 * instantiate the SDK once and access all tracking domains from one object.
 *
 * Every namespace returns an ergonomic *shortcut* wrapper exposing
 * positional-arg methods for the 80% case. The raw underlying class is
 * always available on `.client` for advanced configuration.
 *
 * @example
 * ```ts
 * import { FlowGrid } from "flowgrid-sdk";
 *
 * const fg = new FlowGrid("web_123", "https://core.flow-grid.xyz", "sk_live_xxx");
 *
 * // Top-level shortcuts (the 80% case)
 * fg.track("signup_completed", { plan: "pro" });
 * fg.page();
 * fg.identifyUser("u_1", { email: "alice@example.com" });
 *
 * // Per-feature shortcuts
 * fg.subscriptions.start({ subscriptionId: "sub_1", userId: "u_1", plan: "pro", mrr });
 * if (fg.flags.on("new-checkout")) { ... }
 *
 * // Power-user escape hatch — raw class with full config surface
 * fg.events.client.track({ eventName: "x", sessionId: "...", category: "..." });
 * ```
 */

import { isTrackingSuppressed, setTrackingSuppressed } from "./utils/suppression";
import { FlowGridTransport, FlowGridTransportConfig } from "./client/transport";
import { IdentityManager } from "./utils/identity";
import {
  stashAuthIntent,
  readAuthIntent,
  clearAuthIntent,
  type AuthIntentInput,
} from "./features/analytics/authIntent";
import { hasServerAuthMarker, clearServerAuthMarker } from "./features/analytics/serverAuthMarker";

import { Activation, TrackFeature, TrackPrompt } from "./features/core";
import { FeatureFlags, type FlagContext, type FlagEngineOptions } from "./features/core/flags";
import type { FlagInputContext } from "./features/core/flagEval";

/**
 * Identify traits → flag attributes.
 *
 * Attribute rules compare scalars, so nested objects and arrays are dropped
 * rather than stringified — a rule matching `"[object Object]"` would be worse
 * than a rule that simply never matches.
 */
function flattenTraits(traits: Record<string, unknown>): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(traits)) {
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      out[key] = value;
    }
  }
  return out;
}
import {
  PageViews,
  Sessions,
  Events,
  Identify,
  Funnels,
  Retention,
  Attribution,
  Heatmaps,
  PerformanceTracking,
  SessionReplay,
} from "./features/analytics";
// Ecommerce was removed in SDK 2.0 — revenue comes from provider payments
// (Stripe / Lemon Squeezy / Polar webhooks). Subscriptions survived as their
// own module because nothing else feeds `subscription_states`.
import { Subscriptions } from "./features/subscriptions";
// Enterprise feature + tracker classes are dynamically imported at runtime so
// webpack can split them into a separate async chunk.  The type-only imports
// below give TypeScript full type information without adding any runtime bytes
// to the initial bundle.
import type {
  Engagement,
  ChurnAnalytics,
  Monetization,
  MultiPathFunnels,
  SupportAnalytics,
  AcquisitionAnalytics,
  PathAnalytics,
  SecurityAnalytics,
} from "./features/enterprise";
import type * as _EnterpriseFeaturesNS from "./features/enterprise";

import type {
  EngagementTracker,
  CohortTracker,
  ChurnTracker,
  MonetizationTracker,
  MultiPathFunnelTracker,
  SupportTracker,
  AcquisitionTracker,
  PathTracker,
  AlertTracker,
  SecurityTracker,
  ForecastingTracker,
} from "./tracking/enterprise";
import type * as _EnterpriseTrackingNS from "./tracking/enterprise";

// Import directly from sub-barrels (not the top-level ./tracking barrel) so
// the static module graph never reaches ./tracking/enterprise and its
// dependencies can be tree-shaken / placed only in the async chunk.
import type { CookieBannerConfig } from "./consent/types";
import { grantConsentOnAuth } from "./consent/registry";
import { ensureLocation } from "./utils/geo";
import { ActivationTracker, FeatureTracker, PromptTracker } from "./tracking/core";
import {
  EventTracker, PageViewTracker, SessionTracker, IdentifyTracker,
  FunnelTracker, RetentionTracker, AttributionTracker,
  HeatmapTracker, PerformanceTracker, ReplayTracker,
} from "./tracking/analytics";
import { SubscriptionTracker } from "./tracking/subscriptions";

// ---- Enterprise lazy-load state (module-level singleton) ----
let _enterpriseFeatures: typeof _EnterpriseFeaturesNS | null = null;
let _enterpriseTracking: typeof _EnterpriseTrackingNS | null = null;

function loadEnterpriseModules(): Promise<void> {
  if (_enterpriseFeatures && _enterpriseTracking) return Promise.resolve();
  return Promise.all([
    import(/* webpackChunkName: "flowgrid-enterprise" */ "./features/enterprise"),
    import(/* webpackChunkName: "flowgrid-enterprise" */ "./tracking/enterprise"),
  ]).then(([features, tracking]) => {
    _enterpriseFeatures = features as typeof _EnterpriseFeaturesNS;
    _enterpriseTracking = tracking as typeof _EnterpriseTrackingNS;
  });
}

type Ctor<T> = new (
  webId: string,
  endpoint: string,
  apiKey: string,
  visitorId?: string,
  identityManager?: IdentityManager,
  config?: FlowGridTransportConfig
) => T;

export type FlowGridTrackDomain =
  | "event"
  | "activation"
  | "feature"
  | "prompt"
  | "subscription"
  | "analytics"
  | "retention"
  | "attribution"
  | "funnel"
  | "engagement"
  | "churn"
  | "monetization"
  | "support"
  | "acquisition"
  | "paths"
  | "security"
  | "multiPathFunnels";

export interface FlowGridTrackOptions {
  userId?: string;
  sessionId?: string;
  /** Force a specific module when an event name is intentionally generic. */
  domain?: FlowGridTrackDomain;
}

/**
 * Property shapes for every event name the `.track()` router understands.
 * Required fields are enforced at the call site; extra fields are always
 * permitted via the `Record<string, unknown>` intersection.
 */
export interface FlowGridEventMap {
  // ---- Activation ----
  signup_completed:                              { userId: string; method: string; source?: string } & Record<string, unknown>;
  login_completed:                               { userId: string; method?: string } & Record<string, unknown>;
  onboarding_step:                               { userId: string; stepId: string; stepName: string; stepOrder: number; totalSteps: number } & Record<string, unknown>;
  onboarding_completed:                          { userId: string; totalTimeSpent: number; stepsCompleted: number; stepsSkipped?: number } & Record<string, unknown>;
  first_action:                                  { userId: string; actionName: string; timeSinceSignup: number } & Record<string, unknown>;
  // ---- Feature ----
  feature_usage:                                 { featureId: string; featureName: string; action?: string } & Record<string, unknown>;
  feature_viewed:                                { featureId: string; featureName: string } & Record<string, unknown>;
  feature_used:                                  { featureId: string; featureName: string } & Record<string, unknown>;
  feature_completed:                             { featureId: string; featureName: string } & Record<string, unknown>;
  feature_abandoned:                             { featureId: string; featureName: string } & Record<string, unknown>;
  // ---- Prompt ----
  prompt_submitted:                              { promptId: string; promptType: string } & Record<string, unknown>;
  prompt_response:                               { promptId: string; responseTime: number; completed: boolean } & Record<string, unknown>;
  prompt_feedback:                               { promptId: string; feedbackType: string } & Record<string, unknown>;
  prompt_conversation:                           { conversationId: string; userId: string; turnCount: number; totalDuration: number } & Record<string, unknown>;
  // ---- Analytics ----
  click_event:                                   { elementId: string; elementText: string } & Record<string, unknown>;
  form_event:                                    { formId: string; formName: string } & Record<string, unknown>;
  search_event:                                  { query: string; category: string } & Record<string, unknown>;
  error_event:                                   { message: string; type: string } & Record<string, unknown>;
  funnel_step:                                   { funnelId: string; step: string; stepNumber: number } & Record<string, unknown>;
  funnel_complete:                               { funnelId: string } & Record<string, unknown>;
  funnel_abandon:                                { funnelId: string; lastStep: string } & Record<string, unknown>;
  user_activity:                                 { userId: string } & Record<string, unknown>;
  attribution_touchpoint:                        { userId: string; channel: string } & Record<string, unknown>;
  attribution_conversion:                        { userId: string; conversionType: string } & Record<string, unknown>;
  // ---- Subscriptions ----
  subscription_start:                            { subscriptionId: string; userId: string; plan: string; mrr: Record<string, unknown>; billingCycle: string } & Record<string, unknown>;
  subscription_change:                           { subscriptionId: string; userId: string; previousPlan: string; newPlan: string; previousMrr: Record<string, unknown>; newMrr: Record<string, unknown>; changeType: string } & Record<string, unknown>;
  subscription_cancel:                           { subscriptionId: string; userId: string; plan: string; lostMrr: Record<string, unknown>; immediate: boolean } & Record<string, unknown>;
  subscription_renew:                            { subscriptionId: string; userId: string; mrr: Record<string, unknown> } & Record<string, unknown>;
  trial_converted:                               { subscriptionId: string; userId: string; plan: string; mrr: Record<string, unknown> } & Record<string, unknown>;
  trial_expired:                                 { subscriptionId: string; userId: string } & Record<string, unknown>;
  // Alias of `search_event` — routed to events.trackSearch (see dispatchAnalyticsTrack).
  site_search:                                   { query: string; resultsCount?: number; category?: string } & Record<string, unknown>;
  // ---- Enterprise: Engagement ----
  "engagement.dwell_time":                       { featureId: string; dwellTimeMs: number } & Record<string, unknown>;
  "engagement_dwell_time":                       { featureId: string; dwellTimeMs: number } & Record<string, unknown>;
  "engagement.session_depth":                    { sessionId: string; pagesVisited: number; featuresUsed: number; actionsTaken: number; durationMs: number } & Record<string, unknown>;
  "engagement_session_depth":                    { sessionId: string; pagesVisited: number; featuresUsed: number; actionsTaken: number; durationMs: number } & Record<string, unknown>;
  "engagement.content_consumption":              { contentId: string; contentType: string; percentageConsumed: number; timeSpentMs: number } & Record<string, unknown>;
  "engagement_content_consumption":              { contentId: string; contentType: string; percentageConsumed: number; timeSpentMs: number } & Record<string, unknown>;
  "engagement.user_activity":                    { userId: string; activityType: string; durationMs: number } & Record<string, unknown>;
  "engagement_user_activity":                    { userId: string; activityType: string; durationMs: number } & Record<string, unknown>;
  // ---- Enterprise: Churn ----
  "churn.usage_decay":                           { userId: string; metric: string; periods: unknown } & Record<string, unknown>;
  "churn_usage_decay":                           { userId: string; metric: string; periods: unknown } & Record<string, unknown>;
  "churn.nps":                                   { userId: string; score: number } & Record<string, unknown>;
  "churn_nps":                                   { userId: string; score: number } & Record<string, unknown>;
  "churn.churn_event":                           { userId: string; churnType: string } & Record<string, unknown>;
  "churn_churn_event":                           { userId: string; churnType: string } & Record<string, unknown>;
  "churn.prevention_action":                     { userId: string; actionType: string } & Record<string, unknown>;
  "churn_prevention_action":                     { userId: string; actionType: string } & Record<string, unknown>;
  // ---- Enterprise: Monetization ----
  "monetization.revenue":                        { userId: string; amount: number; currency: string; type: string } & Record<string, unknown>;
  "monetization_revenue":                        { userId: string; amount: number; currency: string; type: string } & Record<string, unknown>;
  // ---- Enterprise: Support ----
  "support.ticket_created":                      { ticketId: string; userId: string; subject: string; category: string; priority: string; channel: string } & Record<string, unknown>;
  "support_ticket_created":                      { ticketId: string; userId: string; subject: string; category: string; priority: string; channel: string } & Record<string, unknown>;
  "support.ticket_updated":                      { ticketId: string; status: string } & Record<string, unknown>;
  "support_ticket_updated":                      { ticketId: string; status: string } & Record<string, unknown>;
  "support.csat":                                { ticketId: string; userId: string; score: number } & Record<string, unknown>;
  "support_csat":                                { ticketId: string; userId: string; score: number } & Record<string, unknown>;
  "support.feature_complaint":                   { userId: string; featureId: string; complaintType: string; severity: string } & Record<string, unknown>;
  "support_feature_complaint":                   { userId: string; featureId: string; complaintType: string; severity: string } & Record<string, unknown>;
  // ---- Enterprise: Acquisition ----
  "acquisition.acquired":                        { userId: string; source: string } & Record<string, unknown>;
  "acquisition_acquired":                        { userId: string; source: string } & Record<string, unknown>;
  "acquisition.lifecycle_transition":            { userId: string; fromStage: string; toStage: string } & Record<string, unknown>;
  "acquisition_lifecycle_transition":            { userId: string; fromStage: string; toStage: string } & Record<string, unknown>;
  "acquisition.campaign_spend":                  { campaignId: string; amount: Record<string, unknown>; date: string } & Record<string, unknown>;
  "acquisition_campaign_spend":                  { campaignId: string; amount: Record<string, unknown>; date: string } & Record<string, unknown>;
  // ---- Enterprise: Paths ----
  "paths.transition":                            { userId: string; fromScreen: string; toScreen: string } & Record<string, unknown>;
  "paths_transition":                            { userId: string; fromScreen: string; toScreen: string } & Record<string, unknown>;
  "paths.feature_sequence":                      { userId: string; feature: string; order: number } & Record<string, unknown>;
  "paths_feature_sequence":                      { userId: string; feature: string; order: number } & Record<string, unknown>;
  // ---- Enterprise: Security ----
  "security.login_attempt":                      { userId: string; success: boolean } & Record<string, unknown>;
  "security_login_attempt":                      { userId: string; success: boolean } & Record<string, unknown>;
  "security.role_change":                        { userId: string; changedBy: string; previousRole: string; newRole: string } & Record<string, unknown>;
  "security_role_change":                        { userId: string; changedBy: string; previousRole: string; newRole: string } & Record<string, unknown>;
  "security.permission_change":                  { userId: string; changedBy: string; permission: string; action: string } & Record<string, unknown>;
  "security_permission_change":                  { userId: string; changedBy: string; permission: string; action: string } & Record<string, unknown>;
  "security.data_access":                        { userId: string; resourceType: string; accessType: string } & Record<string, unknown>;
  "security_data_access":                        { userId: string; resourceType: string; accessType: string } & Record<string, unknown>;
  "security.compliance_event":                   { userId: string; eventType: string } & Record<string, unknown>;
  "security_compliance_event":                   { userId: string; eventType: string } & Record<string, unknown>;
  // ---- Enterprise: Multi-path Funnels ----
  "multi_path_funnels.track_step":               { funnelId: string; step: string; userId: string } & Record<string, unknown>;
  "multi_path_funnels_track_step":               { funnelId: string; step: string; userId: string } & Record<string, unknown>;
  "multi_path_funnels.track_micro_conversion":   { conversionId: string; userId: string } & Record<string, unknown>;
  "multi_path_funnels_track_micro_conversion":   { conversionId: string; userId: string } & Record<string, unknown>;
}

/** Union of every event name the `.track()` router recognises. */
export type FlowGridEventName = keyof FlowGridEventMap;

type FlowGridKnownTrackFields = Partial<{
  userId: string;
  sessionId: string;
  method: string;
  source: string;
  featureId: string;
  featureName: string;
  action: string;
  promptId: string;
  promptType: string;
  responseTime: number;
  completed: boolean;
  feedbackType: string;
  conversationId: string;
  turnCount: number;
  totalDuration: number;
  variant: string;
  value: number;
  elementId: string;
  elementText: string;
  formId: string;
  formName: string;
  query: string;
  category: string;
  message: string;
  type: string;
  funnelId: string;
  step: string;
  stepNumber: number;
  lastStep: string;
  channel: string;
  conversionType: string;
  list: Record<string, unknown>;
  product: Record<string, unknown>;
  productId: string;
  name: string;
  price: number;
  currency: string;
  position: number;
  quantity: number;
  previousQuantity: number;
  newQuantity: number;
  cart: Record<string, unknown>;
  shipping: Record<string, unknown>;
  payment: Record<string, unknown>;
  order: Record<string, unknown>;
  orderId: string;
  refundId: string;
  amount: unknown;
  approvedAmount: number;
  processedAmount: number;
  reason: string;
  promotionId: string;
  creative: string;
  code: string;
  discountValue: number;
  discountType: string;
  orderValueBefore: number;
  orderValueAfter: number;
  wishlist: Record<string, unknown>;
  wishlistId: string;
  subscriptionId: string;
  plan: string;
  mrr: Record<string, unknown>;
  billingCycle: string;
  previousPlan: string;
  newPlan: string;
  previousMrr: Record<string, unknown>;
  newMrr: Record<string, unknown>;
  changeType: string;
  lostMrr: Record<string, unknown>;
  cost: Record<string, unknown>;
  newSegment: string;
  resultsCount: number;
  originalQuery: string;
  refinedQuery: string;
  refinementType: string;
  partialQuery: string;
  selectedSuggestion: string;
  suggestionPosition: number;
  dwellTimeMs: number;
  pagesVisited: number;
  featuresUsed: number;
  actionsTaken: number;
  durationMs: number;
  contentId: string;
  contentType: string;
  percentageConsumed: number;
  timeSpentMs: number;
  metric: string;
  periods: unknown;
  score: number;
  churnType: string;
  actionType: string;
  activityType: string;
  ticketId: string;
  subject: string;
  priority: string;
  status: string;
  complaintType: string;
  severity: string;
  fromStage: string;
  toStage: string;
  campaignId: string;
  date: string;
  fromScreen: string;
  toScreen: string;
  feature: string;
  success: boolean;
  isFullRefund: boolean;
  immediate: boolean;
  changedBy: string;
  previousRole: string;
  newRole: string;
  permission: string;
  resourceType: string;
  accessType: string;
  eventType: string;
  conversionId: string;
}>;

export type FlowGridTrackProperties = Record<string, unknown> & FlowGridKnownTrackFields;

type TrackDispatchResult = { handled: true; value: unknown } | { handled: false };
type UnifiedFeatureAction = "viewed" | "used" | "completed" | "abandoned";

/** The lifecycle action a {@link FeatureHandle} emits. */
export type FeatureHandleAction = UnifiedFeatureAction;

/**
 * A feature bound to its id/name (+ optional category) via
 * {@link FlowGrid.defineFeature}. Call the verb that describes what happened;
 * each emits a `feature_usage` event through the unified `.track()` router.
 */
export interface FeatureHandle {
  readonly featureId: string;
  readonly featureName: string;
  /** Visitor opened / looked at the feature → Discovered + Viewed. */
  viewed(properties?: Record<string, unknown>): unknown;
  /** Visitor used the feature → Adopted. */
  used(properties?: Record<string, unknown>): unknown;
  /** Visitor finished the feature's job → Completed. */
  completed(properties?: Record<string, unknown>): unknown;
  /** Visitor started but gave up → Abandonment. */
  abandoned(properties?: Record<string, unknown>): unknown;
  /** Escape hatch — emit any action explicitly. */
  track(action: FeatureHandleAction, properties?: Record<string, unknown>): unknown;
}

const UNHANDLED_TRACK: TrackDispatchResult = { handled: false };
const PRODUCT_KEYS = [
  "productId",
  "name",
  "brand",
  "category",
  "variant",
  "price",
  "currency",
  "quantity",
  "discount",
  "coupon",
  "position",
  "url",
  "imageUrl",
  "rating",
  "reviewCount",
  "stockStatus",
  "attributes",
] as const;

function handled(value: unknown): TrackDispatchResult {
  return { handled: true, value };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringValue(props: FlowGridTrackProperties, key: string): string | undefined {
  const value = props[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function numberValue(props: FlowGridTrackProperties, key: string): number | undefined {
  const value = props[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function booleanValue(props: FlowGridTrackProperties, key: string): boolean | undefined {
  const value = props[key];
  return typeof value === "boolean" ? value : undefined;
}

function recordValue(props: FlowGridTrackProperties, key: string): FlowGridTrackProperties | undefined {
  const value = props[key];
  return isRecord(value) ? value as FlowGridTrackProperties : undefined;
}

function hasValue(props: FlowGridTrackProperties, key: string): boolean {
  const value = props[key];
  return value !== undefined && value !== null && !(typeof value === "string" && value.length === 0);
}

function omitProperties(props: FlowGridTrackProperties, keys: readonly string[]): FlowGridTrackProperties {
  const next: FlowGridTrackProperties = {};
  const blocked = new Set([...keys, "domain"]);
  for (const [key, value] of Object.entries(props)) {
    if (!blocked.has(key)) next[key] = value;
  }
  return next;
}

function productFrom(props: FlowGridTrackProperties): FlowGridTrackProperties | undefined {
  const nested = recordValue(props, "product");
  if (nested) return { ...nested, currency: nested.currency ?? "USD" };

  if (!hasValue(props, "productId") || !hasValue(props, "name") || !hasValue(props, "price")) {
    return undefined;
  }

  const product: FlowGridTrackProperties = { currency: "USD" };
  for (const key of PRODUCT_KEYS) {
    if (props[key] !== undefined) (product as Record<string, unknown>)[key] = props[key];
  }
  return product;
}

// ============================================================================
// PUBLIC INIT TYPES
// ============================================================================

/** Granular auto-instrumentation toggles. */
export interface AutoTrackOptions {
  sessions?: boolean;
  pageViews?: boolean;
  performance?: boolean | { resources?: boolean; minDuration?: number };
  heatmaps?: boolean | { movement?: boolean };
  attribution?: boolean;
  replay?: boolean | import("./features/analytics/replay").SessionReplayOptions;
  /** Auto-track session depth + user activity transitions. Default on. */
  engagement?: boolean | { idleAfterMs?: number; pollMs?: number };
  /** Recognise anonymous visitors from any form (PII hashed in-browser).
   *  Works both here in `init({ autoTrack: { passiveIdentity: true } })` and via
   *  the `fg.autoTrack({ passiveIdentity: true })` method — same behaviour. */
  passiveIdentity?: boolean | import("./features/analytics/identify").PassiveIdentityOptions;
}

/** Top-level config for `FlowGrid.init()`. */
export interface FlowGridInitConfig {

  /** Public web/site identifier (e.g. `"wx_abc123"`). Required. */
  webId: string;

  /**
   * Public API key. Optional — when omitted, the SDK runs in `data-site`-only
   * mode (web_id body-auth), matching the core `flowgrid.js` snippet. This is
   * what lets a no-build / script-tag install read feature flags. Provide a key
   * for full server-authenticated ingestion.
   */
  apiKey?: string;

  /**
   * API base URL. Defaults to `"https://core.flow-grid.xyz"` — most users never
   * need to set this.
   */
  endpoint?: string;

  /**
   * Identify the user up-front so every subsequent event is attributed.
   * Equivalent to calling `fg.identifyUser(userId, { email, name, ...traits })`
   * after init. `email`/`name` are top-level conveniences (parity with the
   * Known-visitor identity) merged into `traits`.
   */
  user?: { userId: string; email?: string; name?: string; traits?: Record<string, unknown> };

  /**
   * Extra context for feature-flag targeting — plan tier, region, environment,
   * or any custom attribute your rules read. Optional: `webId` and the
   * identified `userId` are filled in for you, and a rule whose input is missing
   * simply doesn't match.
   *
   * @example
   * ```ts
   * FlowGrid.init({ webId, flagContext: { environment: "production", tier: "pro" } });
   * ```
   */
  flagContext?: FlagInputContext;

  /**
   * How flag answers are fetched and kept — delivery, not targeting.
   *
   * The default (`persistenceUntilNetworkSuccess`, 24h) is what the SDK has
   * always done: a returning visitor evaluates correctly on the first render and
   * with the network off. Override it when a stale first frame is worse for you
   * than a default one.
   *
   * @example
   * ```ts
   * Flowgrid.init({ webId, flags: { lookupPolicy: "networkFirst" } });
   * ```
   */
  flags?: FlagEngineOptions;

  /**
   * Browser auto-instrumentation. `true` (default) turns on engagement and
   * session replay. Pageviews, sessions, heatmaps, performance, and attribution
   * are handled by the flowgrid.js core script and are off by default here.
   * Pass an object to control individual features, or `false` to opt out completely.
   */
  autoTrack?: boolean | AutoTrackOptions;

  /**
   * Session replay (rrweb). Off by default — opt in by passing `true` or an
   * options object. Requires the `rrweb` package (already a dependency).
   */
  replay?: boolean | import("./features/analytics/replay").SessionReplayOptions;

  /**
   * Initial consent state. Equivalent to calling
   * `FlowGrid.setConsent({ analytics, marketing })` before any tracking.
   */
  consent?: { analytics?: boolean; marketing?: boolean };

  /**
   * Declarative cookie-consent banner config for the no-code path. Set this on
   * `window.FlowGridConfig.consentBanner` (or pass it here) and a
   * `<div data-cookie-ccbanner>` on the page renders a styled banner — no JS.
   * Defaults to opt-out (nothing is gated); set `mode: 'opt-in'` to gate
   * analytics/marketing/replay until the visitor accepts.
   * Per-element `data-cc-*` attributes override these defaults.
   */
  consentBanner?: CookieBannerConfig;

  /** Low-level transport tunables (sampling, DNT, bot detection). */
  transport?: FlowGridTransportConfig;

  /** Optional pre-resolved visitor ID (server-side hydration). */
  visitorId?: string;

  /** Optional identity manager (advanced — visitor/session lifecycle). */
  identityManager?: IdentityManager;
  
}

const DEFAULT_ENDPOINT = "https://core.flow-grid.xyz";

/**
 * Unified FlowGrid SDK client. Instantiate once, use everywhere.
 *
 * Each feature is exposed as a lazily-initialised shortcut wrapper, so the
 * SDK has zero overhead for unused features.
 */
export class FlowGrid {
  /** Singleton created by {@link FlowGrid.init}. */
  private static _instance: FlowGrid | null = null;
  /** Teardown for the auto-instrumentation started during `init()`. */
  private _autoTrackStop: (() => void) | null = null;

  private readonly webId: string;
  private readonly endpoint: string;
  private readonly apiKey: string;
  private readonly visitorId?: string;
  private readonly identityManager?: IdentityManager;
  private readonly config?: FlowGridTransportConfig;

  private readonly _raw = new Map<string, unknown>();
  private readonly _wrapped = new Map<string, unknown>();

  /** Last identified user id — used by {@link logout} when none is passed. */
  private _identifiedUserId: string | null = null;

  /**
   * In-memory fallback for the once-per-day {@link pingActiveUser} dedupe,
   * used when localStorage is unavailable (privacy mode, non-browser runtime).
   */
  private _lastActivePing: string | null = null;

  /**
   * One-line init. Idempotent — calling twice returns the same instance and
   * logs a warning instead of starting auto-instrumentation again.
   *
   * @example
   * ```ts
   * // Painless: one line, sane defaults, everything on.
   * const fg = FlowGrid.init({ webId: "wx_xxx", apiKey: "pk_xxx" });
   *
   * // Or be explicit:
   * const fg = FlowGrid.init({
   *   webId: "wx_xxx",
   *   apiKey: "pk_xxx",
   *   user: { userId: "u_1", traits: { plan: "pro" } },
   *   replay: { sampleRate: 1.0, maskAllInputs: true },
   *   consent: { analytics: true },
   * });
   * ```
   */
  static init(config: FlowGridInitConfig): FlowGrid {
    if (FlowGrid._instance) {
      if (typeof console !== "undefined") {
        console.warn("[FlowGrid] init() called more than once — returning existing instance.");
      }
      return FlowGrid._instance;
    }

    if (!config?.webId) {
      throw new Error("FlowGrid.init: `webId` is required.");
    }

    if (config.consent) {
      FlowGrid.setConsent(config.consent);
    }

    const fg = new FlowGrid(
      config.webId,
      config.endpoint ?? DEFAULT_ENDPOINT,
      config.apiKey ?? "",
      config.visitorId,
      config.identityManager,
      config.transport,
    );
    FlowGrid._instance = fg;

    // Pre-fetch the enterprise chunk immediately so getters are ready by the
    // time user code first accesses them (typically well after init returns).
    void loadEnterpriseModules();

    // Load feature flags immediately, before identify and auto-tracking. A flag
    // read is synchronous and can happen on the app's very first render, so the
    // fetch has to be in flight as early as possible — and `flags.ready()` is
    // what a UI awaits instead of flashing the default. Fire-and-forget: init
    // must never wait on it, and the engine never rejects.
    //
    // The visitor id is not passed here: `load()` reads it from the identity
    // manager it was constructed with, so percentage rollouts split anonymous
    // traffic even when the app never calls `identify`.
    void fg.flags.load(
      {
        webId: fg.webId,
        ...(config.user?.userId ? { userId: config.user.userId } : {}),
        ...(config.flagContext ?? {}),
      },
      config.flags,
    ).catch(() => null);

    // Identify the user before auto-instrumentation so the very first events
    // (session_start, page_view) carry the userId. `email`/`name` are accepted
    // at the top level for parity with the Known-visitor identity and merged
    // into traits; identifying an existing session also marks the user active
    // (DAU/WAU/MAU) so returning users are counted without an explicit login.
    if (config.user?.userId) {
      const { userId, email, name, traits } = config.user;
      const merged: Record<string, unknown> = {
        ...(email ? { email } : {}),
        ...(name ? { name } : {}),
        ...(traits ?? {}),
      };
      void fg.identifyUser(userId, merged);
      void fg.pingActiveUser(userId, merged);
    }

    const autoTrack = config.autoTrack;
    const replay = config.replay;
    const autoOpts: AutoTrackOptions =
      autoTrack === false ? { sessions: false, pageViews: false, performance: false, heatmaps: false, attribution: false, engagement: false, replay: false }
      : autoTrack === true || autoTrack === undefined ? { sessions: false, pageViews: false, performance: false, heatmaps: false, attribution: false }
      : autoTrack;

    // Top-level `replay` always wins over autoTrack defaults. Previously this
    // only applied when autoOpts.replay was undefined, which meant
    // `init({ autoTrack: false, replay: true })` silently disabled replay.
    if (replay !== undefined) {
      autoOpts.replay = replay;
    }

    // Stand down against the CDN tracker rather than requiring the caller to
    // know to. `window.FlowGrid` is set by `browser.ts` (the CDN build) — a
    // SEPARATE bundle with its own copy of this class, so its `_instance` is
    // invisible to this one and vice versa (see `resolve.ts`'s "class first,
    // window second" comment). Two live instances both auto-capturing the
    // same stream is silent double counting, and it used to be the caller's
    // job to remember to turn every overlapping stream off by hand. Now: any
    // stream the caller did NOT explicitly name in `autoTrack` is forced off
    // when a CDN instance is already on the page. A stream they DID name is
    // left alone — an explicit choice, even an unusual one, is respected.
    if (typeof window !== "undefined" && (window as unknown as { FlowGrid?: unknown }).FlowGrid) {
      const explicit = new Set(
        autoTrack && typeof autoTrack === "object" ? Object.keys(autoTrack) : [],
      );
      // Top-level `replay` (handled above) is its own explicit choice, same as
      // naming `replay` inside `autoTrack`.
      if (replay !== undefined) explicit.add("replay");

      const captureKeys: (keyof AutoTrackOptions)[] = [
        "sessions", "pageViews", "performance", "heatmaps", "attribution", "engagement", "passiveIdentity", "replay",
      ];
      for (const key of captureKeys) {
        if (!explicit.has(key)) {
          (autoOpts as Record<string, unknown>)[key] = false;
        }
      }
      if (typeof console !== "undefined") {
        console.info(
          "[Flowgrid] the CDN tracker is already on this page (window.FlowGrid) — auto-capture streams you did not explicitly pass to `autoTrack` are disabled here to keep this instance from double-counting them. Pass `autoTrack: { <stream>: true }` (or top-level `replay`) to capture a stream from both anyway.",
        );
      }
    }

    fg._autoTrackStop = fg.autoTrack({
      ...autoOpts,
      userId: config.user?.userId ?? (autoOpts as { userId?: string }).userId,
    });

    // Resolve (and report) the visitor's location. The SDK previously shipped
    // NO geo at all, so a site tracked only by this SDK reported no country
    // for anyone, and every SDK-identified visitor was unlocatable forever.
    //
    // NOT configurable, and deliberately not gated behind `autoTrack`. Location
    // is not a capture stream you opt into — it is a property of the visitor
    // that identify, sessions, the visitor map and every geo breakdown read.
    // A switch that turns it off doesn't produce "less data", it produces
    // partially-located data: some visitors with a country and some without,
    // in the same charts, silently skewing every geo aggregate downstream. A
    // dashboard that can't tell "nobody from Japan" from "geo was off that
    // month" is worse than no dashboard. Consent is handled by the transport
    // gates, which is the correct place to stop collection.
    //
    // Resolution is memoized and mirrored to the `fg_geo` cookie, so this is
    // one lookup per visitor, not per page. Fire-and-forget — init must never
    // wait on a third-party geo call.
    try {
      const resolvedVisitorId = fg.identityManager?.getVisitorId() ?? fg.visitorId ?? "";
      void ensureLocation(fg.endpoint, fg.webId, resolvedVisitorId).catch(() => null);
    } catch {
      /* geo is enrichment — never let it break init */
    }

    return fg;
  }

  /** Returns the singleton created by {@link FlowGrid.init}, if any. */
  static instance(): FlowGrid | null {
    return FlowGrid._instance;
  }

  /**
   * Await the enterprise feature chunk. Call this before accessing any
   * enterprise namespace (`fg.engagement`, `fg.churn`, etc.) if you need
   * guaranteed availability rather than relying on the background pre-fetch
   * started by `init()`.
   */
  static loadEnterprise(): Promise<void> {
    return loadEnterpriseModules();
  }

  /**
   * Reset the singleton — primarily for tests / hot-reload. Tears down any
   * active auto-instrumentation.
   */
  static reset(): void {
    if (FlowGrid._instance?._autoTrackStop) {
      try { FlowGrid._instance._autoTrackStop(); } catch { /* noop */ }
    }
    FlowGrid._instance = null;
  }

  /**
   * Explicit constructor for advanced cases (multi-tenant SSR, tests). Most
   * apps should use the static {@link FlowGrid.init} instead.
   */
  constructor(
    webId: string,
    endpoint: string,
    apiKey: string,
    visitorId?: string,
    identityManager?: IdentityManager,
    config?: FlowGridTransportConfig
  ) {
    this.webId = webId;
    this.endpoint = endpoint;
    this.apiKey = apiKey;
    this.visitorId = visitorId;
    this.identityManager = identityManager ?? new IdentityManager(visitorId ? { visitorId } : undefined);
    this.config = config;
  }

  private raw<T>(key: string, Klass: Ctor<T>): T {
    let instance = this._raw.get(key) as T | undefined;
    if (!instance) {
      instance = new Klass(
        this.webId,
        this.endpoint,
        this.apiKey,
        this.visitorId,
        this.identityManager,
        this.config
      );
      this._raw.set(key, instance);
    }
    return instance;
  }

  private wrap<W, T>(key: string, Klass: Ctor<T>, Wrapper: new (client: T) => W): W {
    let instance = this._wrapped.get(key) as W | undefined;
    if (!instance) {
      instance = new Wrapper(this.raw(key, Klass));
      this._wrapped.set(key, instance);
    }
    return instance;
  }

  // ---- Core ----
  get activation(): ActivationTracker { return this.wrap("activation", Activation, ActivationTracker); }
  get features(): FeatureTracker { return this.wrap("features", TrackFeature, FeatureTracker); }
  get prompts(): PromptTracker { return this.wrap("prompts", TrackPrompt, PromptTracker); }

  /**
   * Feature flags. Unlike the other namespaces there is no `Tracker` wrapper —
   * the engine IS the API surface, and it is deliberately three methods:
   *
   * ```ts
   * if (fg.flags.on("new-checkout")) { ... }
   * await fg.flags.ready();
   * fg.flags.onChange(rerender);
   * ```
   */
  get flags(): FeatureFlags { return this.raw("flags", FeatureFlags); }

  // ---- Analytics ----
  get pageViews(): PageViewTracker { return this.wrap("pageViews", PageViews, PageViewTracker); }
  get sessions(): SessionTracker { return this.wrap("sessions", Sessions, SessionTracker); }
  get events(): EventTracker { return this.wrap("events", Events, EventTracker); }
  get identify(): IdentifyTracker { return this.wrap("identify", Identify, IdentifyTracker); }
  get funnels(): FunnelTracker { return this.wrap("funnels", Funnels, FunnelTracker); }
  get retention(): RetentionTracker { return this.wrap("retention", Retention, RetentionTracker); }
  get attribution(): AttributionTracker { return this.wrap("attribution", Attribution, AttributionTracker); }
  get heatmaps(): HeatmapTracker { return this.wrap("heatmaps", Heatmaps, HeatmapTracker); }
  get performance(): PerformanceTracker { return this.wrap("performance", PerformanceTracking, PerformanceTracker); }
  get replay(): ReplayTracker { return this.wrap("replay", SessionReplay, ReplayTracker); }

  // ---- Subscriptions ----
  get subscriptions(): SubscriptionTracker { return this.wrap("subscriptions", Subscriptions, SubscriptionTracker); }

  // ---- Enterprise ----
  // These getters pull classes from the lazily-loaded enterprise chunk.
  // The chunk is pre-fetched by init() so it's almost always ready by the
  // time user code accesses these properties.  loadEnterprise() is available
  // for the rare case where callers need to await readiness explicitly.
  private _requireEnterprise() {
    const ent = _enterpriseFeatures;
    const entT = _enterpriseTracking;
    if (!ent || !entT) {
      throw new Error(
        "[FlowGrid] Enterprise features are still loading. " +
        "await FlowGrid.loadEnterprise() before accessing enterprise namespaces directly."
      );
    }
    return { ent, entT };
  }

  get engagement(): EngagementTracker { const { ent, entT } = this._requireEnterprise(); return this.wrap("engagement", ent.Engagement, entT.EngagementTracker); }
  get cohorts(): CohortTracker { const { ent, entT } = this._requireEnterprise(); return this.wrap("cohorts", ent.Cohorts, entT.CohortTracker); }
  get churn(): ChurnTracker { const { ent, entT } = this._requireEnterprise(); return this.wrap("churn", ent.ChurnAnalytics, entT.ChurnTracker); }
  get monetization(): MonetizationTracker { const { ent, entT } = this._requireEnterprise(); return this.wrap("monetization", ent.Monetization, entT.MonetizationTracker); }
  get multiPathFunnels(): MultiPathFunnelTracker { const { ent, entT } = this._requireEnterprise(); return this.wrap("multiPathFunnels", ent.MultiPathFunnels, entT.MultiPathFunnelTracker); }
  get support(): SupportTracker { const { ent, entT } = this._requireEnterprise(); return this.wrap("support", ent.SupportAnalytics, entT.SupportTracker); }
  get acquisition(): AcquisitionTracker { const { ent, entT } = this._requireEnterprise(); return this.wrap("acquisition", ent.AcquisitionAnalytics, entT.AcquisitionTracker); }
  get paths(): PathTracker { const { ent, entT } = this._requireEnterprise(); return this.wrap("paths", ent.PathAnalytics, entT.PathTracker); }
  get alerts(): AlertTracker { const { ent, entT } = this._requireEnterprise(); return this.wrap("alerts", ent.AlertsAnalytics, entT.AlertTracker); }
  get security(): SecurityTracker { const { ent, entT } = this._requireEnterprise(); return this.wrap("security", ent.SecurityAnalytics, entT.SecurityTracker); }
  get forecasting(): ForecastingTracker { const { ent, entT } = this._requireEnterprise(); return this.wrap("forecasting", ent.Forecasting, entT.ForecastingTracker); }

  // ---- Consent passthrough (static on transport) ----
  static setConsent = FlowGridTransport.setConsent.bind(FlowGridTransport);
  static hasConsent = FlowGridTransport.hasConsent.bind(FlowGridTransport);

  // ==========================================================================
  // TOP-LEVEL SHORTCUTS — the 80% case
  // ==========================================================================

  /**
   * Track a semantic event. The `eventName` must be one of the recognised
   * {@link FlowGridEventName} literals — TypeScript will error on unknown
   * names and narrow `properties` to the required shape for that event.
   *
   * @example
   * ```ts
   * fg.track("signup_completed", { userId: "u_1", method: "google" });
   * fg.track("add_to_cart", { productId: "sku_1", name: "Tee", price: 29, currency: "USD", quantity: 1 });
   * ```
   */
  track<E extends FlowGridEventName>(
    eventName: E,
    properties: FlowGridEventMap[E],
    options?: FlowGridTrackOptions
  ): unknown;
  track(
    eventName: string,
    properties: FlowGridTrackProperties = {},
    options: FlowGridTrackOptions = {}
  ) {
    const contextualProperties = this.withTrackContext(properties, options);
    const routed = this.dispatchTrack(eventName, contextualProperties, Boolean(options.domain), options.domain);
    if (routed.handled) return routed.value;
    return this.events.track(eventName, properties, options);
  }

  /**
   * Record a completed signup. Populates the dashboard Acquisition Funnel's
   * "Signed up" stage (written to goals as `action_name = "signup"`).
   *
   * @example
   * ```ts
   * fg.signup("u_123", "google");           // userId, method
   * fg.signup("u_123", "email", "pricing"); // + traffic source
   * ```
   */
  signup(userId: string, method: string, source?: string, properties?: Record<string, unknown>) {
    return this.activation.signup(userId, method, source, properties);
  }

  /**
   * Record a returning-user login. Tracked as an event (retention / returning
   * signal), not a funnel conversion.
   *
   * @example
   * ```ts
   * fg.login("u_123", "google");
   * ```
   */
  login(userId: string, method?: string, properties?: Record<string, unknown>) {
    return this.activation.login(userId, method, properties);
  }

  /**
   * Globally suppress ALL FlowGrid tracking for this visitor, or lift it.
   *
   * This is the kill switch, not a consent category: it outranks consent,
   * sampling, DNT settings and every per-feature flag. While set, no events, no
   * identity, no session replay and no heartbeats leave the browser.
   *
   * It is stored as a first-party `fg_ignore` cookie rather than in
   * localStorage, so it travels with every request to the origin and the
   * server/ingest side honours the same opt-out — otherwise a server-rendered
   * page would keep recording a visitor who had opted out.
   *
   * The native tracker's `localStorage.fs_ignore` flag (`flowgrid.js`) is read
   * as an equivalent, and the two are kept in step — so setting either one
   * silences the npm SDK, the native tracker and server-side ingest alike.
   *
   * @param suppressed - `true` (default) to opt out, `false` to opt back in.
   *
   * @example
   * ```ts
   * fg.suppressTracking();       // internal user / QA session — record nothing
   * fg.suppressTracking(false);  // opt back in
   * ```
   */
  suppressTracking(suppressed = true): void {
    setTrackingSuppressed(suppressed);
  }

  /** Whether the global kill switch is currently set for this visitor. */
  isTrackingSuppressed(): boolean {
    return isTrackingSuppressed();
  }

  // ==========================================================================
  // AUTH — login / signup (React, Next, or plain JS)
  // ==========================================================================

  /**
   * Mark that a login is about to happen, BEFORE an auth redirect. Stashes the
   * intent (method / source / originating page) in sessionStorage so the
   * matching {@link completeAuth} — fired after the redirect resolves, in any
   * framework — attributes the login to the right page and fires the event.
   * No-op outside the browser.
   *
   * @example
   * ```ts
   * fg.markLogin({ method: "google" });
   * await signIn("google"); // provider redirects away
   * ```
   */
  markLogin(opts: AuthIntentInput = {}) {
    stashAuthIntent("login", opts);
  }

  /** Mark that a signup is about to happen, before an auth redirect. See {@link markLogin}. */
  markSignup(opts: AuthIntentInput = {}) {
    stashAuthIntent("signup", opts);
  }

  /**
   * Complete an auth flow once the user id is known (e.g. after an OAuth
   * redirect resolves). Identifies the user — writing the identity store so they
   * appear as a Known visitor with email/name — then fires the matching
   * signup/login event using the intent stashed by {@link markLogin} /
   * {@link markSignup} (defaults to login when none was stashed), and marks the
   * user active for DAU/WAU/MAU. Call it from a React effect, a Next route
   * handler, or plain JS — wherever the id lands.
   *
   * Also auto-accepts cookie consent: logging in counts as opting in to
   * tracking, so every {@link ConsentManager} (banner or headless) runs
   * `acceptAll()` and consent-gated streams start. Disable per manager with
   * `grantOnAuth: false`.
   *
   * @example
   * ```ts
   * fg.completeAuth(user.id, { email: user.email, name: user.name });
   * ```
   */
  completeAuth(
    userId: string,
    traits?: { email?: string; name?: string } & Record<string, unknown>,
  ) {
    const intent = readAuthIntent();
    // Identify first so this event — and every later event — is attributed, and
    // the identity store carries email/name for the Known-visitor / active-user
    // loops.
    const identifyResult = this.identifyUser(userId, traits);

    const mode = intent?.mode ?? "login";
    const method = intent?.method ?? "email";
    // Attribute the event to the page auth actually started on, not wherever
    // completeAuth runs (usually a post-login route).
    const origin: Record<string, unknown> = {};
    if (intent?.path) origin._page = intent.path;
    if (intent?.url) origin._url = intent.url;

    const eventResult =
      mode === "signup"
        ? this.signup(userId, method, intent?.source, { ...traits, ...origin })
        : this.login(userId, method, { ...traits, ...origin });

    this.pingActiveUser(userId, traits);
    clearAuthIntent();
    return { identify: identifyResult, event: eventResult };
  }

  /**
   * Beacon-style auth completion: identical to {@link completeAuth}, but a
   * **no-op unless a {@link markLogin} / {@link markSignup} intent is pending**.
   * That makes it safe to call every time your app mounts or the user id
   * resolves — it fires exactly once, right after the auth redirect, instead
   * of logging a phantom "login" on every page load. This replaces the
   * hand-rolled tracking-beacon component apps used to need.
   *
   * @example
   * ```tsx
   * // React — the entire auth-tracking "beacon" is one effect:
   * useEffect(() => {
   *   if (user) fg.completeAuthIfPending(user.id, { email: user.email, name: user.name });
   * }, [user]);
   * ```
   */
  completeAuthIfPending(
    userId: string,
    traits?: { email?: string; name?: string } & Record<string, unknown>,
  ) {
    if (!readAuthIntent()) return undefined;
    return this.completeAuth(userId, traits);
  }

  /**
   * The one-call session beacon: tell FlowGrid who is signed in and it handles
   * everything an auth-tracking component used to hand-roll. Call it whenever
   * your app has (or resolves) an authenticated user — every mount, every
   * render, every route change. Every step is idempotent/deduped, so repeat
   * calls are free:
   *
   * 1. **Consent** — logging in = accepting tracking; every registered
   *    {@link ConsentManager} gets `acceptAll()` (opt out per manager with
   *    `grantOnAuth: false`).
   * 2. **Pending auth intent** — if {@link markLogin} / {@link markSignup}
   *    stashed an intent before a redirect, fires the attributed login/signup
   *    event exactly once (full {@link completeAuth}). Pass
   *    `authEvents: false` when your *server* already emits those events
   *    (e.g. from NextAuth callbacks) so funnels don't double-count.
   * 3. **Identity** — binds this browser's visitor id to the user in the
   *    identity store (once per page load), so they show as a Known visitor
   *    on their real session.
   * 4. **Active user** — {@link pingActiveUser} for DAU/WAU/MAU by identity,
   *    deduped to once per user per day.
   *
   * Pass `null` to clear the user on sign-out (same as {@link logout}).
   * `undefined` / `""` is a no-op — safe while your session is still loading.
   *
   * @example
   * ```tsx
   * // React — the entire auth-tracking "beacon" is one effect:
   * useEffect(() => {
   *   if (user) fg.setUser(user.id, { email: user.email, name: user.name });
   * }, [user]);
   * ```
   * ```ts
   * // Sign-out
   * fg.setUser(null);
   * ```
   */
  setUser(
    userId: string | number | null | undefined,
    traits?: { email?: string; name?: string } & Record<string, unknown>,
    opts?: { authEvents?: boolean },
  ) {
    // Explicit null = sign-out. undefined/"" = "don't know yet" (session still
    // resolving) — a no-op, NOT a logout, so `fg.setUser(user?.id)` is safe to
    // call while auth state loads without resetting the previous identity.
    if (userId === null) {
      this.logout();
      return undefined;
    }
    if (userId === undefined || userId === "") return undefined;
    const id = String(userId);

    // A stashed markLogin/markSignup intent means this is the first call after
    // an auth redirect → run the full completeAuth flow (attributed event +
    // identify + ping + consent), exactly once. When the server already emitted
    // the auth events, just drop the flag so it can't leak into a later flow.
    //
    // `authEvents` unset (the default) no longer just means "fire it" — it
    // means "fire it unless the server already did", checked via the cookie
    // `FlowgridServer.trackAuth` hands its caller to set (see
    // `serverAuthMarker`). This is the ownership rule enforcing itself instead
    // of relying on the app getting `authEvents: false` right on this one
    // call site. An explicit `authEvents` (true OR false) still wins outright.
    if (readAuthIntent()) {
      const serverAlreadyTracked = opts?.authEvents === undefined && hasServerAuthMarker();
      clearServerAuthMarker(); // only ever suppresses the one transition it named
      if (opts?.authEvents !== false && !serverAlreadyTracked) return this.completeAuth(id, traits);
      clearAuthIntent();
    }

    // Bind this browser's visitor id to the user once per page load —
    // identifyUser memoizes via _identifiedUserId.
    if (this._identifiedUserId !== id) void this.identifyUser(id, traits);

    // Daily active-user ping; also auto-grants consent on every call (the
    // grant runs before the ping's own once-per-day throttle).
    return this.pingActiveUser(id, traits);
  }

  /**
   * One-call auth tracking for apps that DON'T redirect (email/password, magic
   * link resolved in-page): identify + fire the login/signup event + mark the
   * user active, in a single call. Prefer {@link markLogin} + {@link completeAuth}
   * when your provider redirects. Like {@link completeAuth}, it auto-accepts
   * cookie consent (logging in = opting in; disable per manager with
   * `grantOnAuth: false`).
   *
   * @example
   * ```ts
   * fg.trackAuth({ event: "login", userId: u.id, email: u.email, name: u.name, method: "password" });
   * ```
   */
  trackAuth(input: {
    event: "login" | "signup";
    userId: string;
    email?: string;
    name?: string;
    method?: string;
    source?: string;
    traits?: Record<string, unknown>;
  }) {
    const traits: Record<string, unknown> = {
      ...(input.email ? { email: input.email } : {}),
      ...(input.name ? { name: input.name } : {}),
      ...(input.traits ?? {}),
    };
    const identifyResult = this.identifyUser(input.userId, traits);
    const method = input.method ?? "email";
    const eventResult =
      input.event === "signup"
        ? this.signup(input.userId, method, input.source, traits)
        : this.login(input.userId, method, traits);
    this.pingActiveUser(input.userId, traits);
    return { identify: identifyResult, event: eventResult };
  }

  /**
   * Emit an "active user" signal for an identified user so the dashboard can
   * compute DAU / WAU / MAU *by identity*. Fired automatically by the auth
   * helpers; call it yourself on app open to also count returning users who
   * don't re-authenticate. Carries email/name for parity with the
   * Known-visitor identity.
   *
   * **Self-throttling** — pings dedupe to once per user per UTC day (the
   * granularity DAU is counted at), so call this as often as you like: every
   * page load, every render, every route change. Repeat same-day calls are
   * dropped before they reach the network and never inflate event volume.
   *
   * @example
   * ```ts
   * // On app boot, if a user session already exists:
   * fg.pingActiveUser(user.id, { email: user.email, name: user.name });
   * ```
   */
  pingActiveUser(
    userId: string,
    traits?: { email?: string; name?: string } & Record<string, unknown>,
  ) {
    // Logging in = accepting tracking: every auth helper (completeAuth,
    // trackAuth) and the once-per-session app-boot ping funnels through here,
    // so this is the one place that knows "this browser belongs to an
    // authenticated user". Auto-accept consent on the app's managers/banners —
    // opt out per manager with `grantOnAuth: false`.
    grantConsentOnAuth();
    // Once per user per UTC day (the same `date` bucket the event carries):
    // DAU/WAU/MAU count distinct users, so repeat same-day pings only burn
    // event volume. Throttle here so no caller ever has to.
    const stamp = `${userId}:${new Date().toISOString().slice(0, 10)}`;
    try {
      if (localStorage.getItem("fg_active_user_ping") === stamp) return undefined;
      localStorage.setItem("fg_active_user_ping", stamp);
    } catch {
      // Storage unavailable (privacy mode, non-browser) — dedupe in memory,
      // which still collapses same-page-load repeats.
      if (this._lastActivePing === stamp) return undefined;
      this._lastActivePing = stamp;
    }
    // Rides the activation pipe (subtype `active_user`) so the wire event_name
    // is `active_user` — the generic custom_event pipe stored it under
    // event_name "custom_event", which the DAU/WAU/MAU pipes can't see.
    return this.activation.trackActiveUser(userId, traits);
  }

  /**
   * Clear the identified user on logout so a shared device doesn't attribute the
   * next visitor to the previous user. Resets the identity server-side and
   * clears any pending auth intent. Pass the user id, or omit it to reset the
   * last-identified user.
   *
   * @example
   * ```ts
   * fg.logout();          // reset the last-identified user
   * fg.logout("u_123");   // reset a specific user
   * ```
   */
  logout(userId?: string) {
    const id = userId ?? this._identifiedUserId ?? undefined;
    clearAuthIntent();
    this._identifiedUserId = null;
    if (id) return this.identify.reset(id);
    return undefined;
  }

  /** Alias for {@link logout}. */
  reset(userId?: string) {
    return this.logout(userId);
  }

  /**
   * Attribution ids for **revenue attribution** — spread the result into
   * Stripe's `metadata` / Lemon Squeezy's `checkoutData.custom` when
   * creating a checkout **client-side**, so your payment webhook can read
   * them back (`attributionFromMetadata` in `flowgrid-sdk/server`) and
   * revenue joins this visitor's session, campaign and journey.
   *
   * When the checkout is created **server-side** (the common case), you
   * don't need this — read the same ids from the request cookies with
   * `checkoutAttributionFromCookies()` in `flowgrid-sdk/server`.
   *
   * @example
   * ```ts
   * const res = await fetch("/api/checkout", {
   *   method: "POST",
   *   body: JSON.stringify({ priceId, ...fg.checkoutAttribution() }),
   * });
   * ```
   */
  checkoutAttribution(): { flowgrid_visitor_id?: string; flowgrid_session_id?: string } {
    const manager = this.identityManager;
    if (!manager) return {};
    try {
      const visitorId = manager.getVisitorId();
      const sessionId = manager.getSessionId();
      return {
        ...(visitorId ? { flowgrid_visitor_id: visitorId } : {}),
        ...(sessionId ? { flowgrid_session_id: sessionId } : {}),
      };
    } catch {
      return {};
    }
  }

  private withTrackContext(
    properties: FlowGridTrackProperties,
    options: FlowGridTrackOptions
  ): FlowGridTrackProperties {
    return {
      ...properties,
      ...(options.userId !== undefined && properties.userId === undefined ? { userId: options.userId } : {}),
      ...(options.sessionId !== undefined && properties.sessionId === undefined ? { sessionId: options.sessionId } : {}),
    };
  }

  private requireTrackProperties(
    eventName: string,
    properties: FlowGridTrackProperties,
    keys: readonly string[],
    strict: boolean
  ): boolean {
    const missing = keys.filter(key => !hasValue(properties, key));
    if (missing.length === 0) return true;
    if (strict) {
      throw new Error(`FlowGrid.track("${eventName}") missing required properties: ${missing.join(", ")}`);
    }
    return false;
  }

  private dispatchTrack(
    eventName: string,
    properties: FlowGridTrackProperties,
    strict: boolean,
    domain?: FlowGridTrackDomain
  ): TrackDispatchResult {
    const dispatchers: Array<() => TrackDispatchResult> = [];

    if (!domain || domain === "activation") dispatchers.push(() => this.dispatchActivationTrack(eventName, properties, strict));

    if (!domain || domain === "feature") dispatchers.push(() => this.dispatchFeatureTrack(eventName, properties, strict));

    if (!domain || domain === "prompt") dispatchers.push(() => this.dispatchPromptTrack(eventName, properties, strict));

    if (!domain || domain === "analytics" || domain === "funnel" || domain === "retention" || domain === "attribution") {
      dispatchers.push(() => this.dispatchAnalyticsTrack(eventName, properties, strict, domain));
    }

    if (!domain || domain === "subscription") dispatchers.push(() => this.dispatchSubscriptionTrack(eventName, properties, strict));

    if (
      !domain ||
      domain === "engagement" ||
      domain === "churn" ||
      domain === "monetization" ||
      domain === "support" ||
      domain === "acquisition" ||
      domain === "paths" ||
      domain === "security" ||
      domain === "multiPathFunnels"
    ) {
      dispatchers.push(() => this.dispatchEnterpriseTrack(eventName, properties, strict, domain));
    }

    for (const dispatch of dispatchers) {
      const result = dispatch();
      if (result.handled) return result;
    }

    return UNHANDLED_TRACK;
  }

  private dispatchActivationTrack(
    eventName: string,
    properties: FlowGridTrackProperties,
    strict: boolean
  ): TrackDispatchResult {
    switch (eventName) {
      case "signup_completed": {
        if (!this.requireTrackProperties(eventName, properties, ["userId", "method"], strict)) return UNHANDLED_TRACK;
        return handled(this.activation.signup(
          stringValue(properties, "userId")!,
          stringValue(properties, "method")!,
          stringValue(properties, "source"),
          omitProperties(properties, ["userId", "method", "source"])
        ));
      }
      case "login_completed": {
        if (!this.requireTrackProperties(eventName, properties, ["userId"], strict)) return UNHANDLED_TRACK;
        return handled(this.activation.login(
          stringValue(properties, "userId")!,
          stringValue(properties, "method"),
          omitProperties(properties, ["userId", "method"])
        ));
      }
      case "onboarding_step": {
        if (!this.requireTrackProperties(eventName, properties, ["userId", "stepId", "stepName", "stepOrder", "totalSteps"], strict)) return UNHANDLED_TRACK;
        return handled(this.activation.onboardingStep(
          stringValue(properties, "userId")!,
          stringValue(properties, "stepId")!,
          stringValue(properties, "stepName")!,
          numberValue(properties, "stepOrder")!,
          numberValue(properties, "totalSteps")!,
          {
            skipped: booleanValue(properties, "skipped"),
            timeSpent: numberValue(properties, "timeSpent"),
            properties: omitProperties(properties, ["userId", "stepId", "stepName", "stepOrder", "totalSteps", "skipped", "timeSpent"]),
          }
        ));
      }
      case "onboarding_completed": {
        if (!this.requireTrackProperties(eventName, properties, ["userId", "totalTimeSpent", "stepsCompleted"], strict)) return UNHANDLED_TRACK;
        return handled(this.activation.onboardingComplete(
          stringValue(properties, "userId")!,
          numberValue(properties, "totalTimeSpent")!,
          numberValue(properties, "stepsCompleted")!,
          numberValue(properties, "stepsSkipped") ?? 0
        ));
      }
      case "first_action": {
        if (!this.requireTrackProperties(eventName, properties, ["userId", "actionName", "timeSinceSignup"], strict)) return UNHANDLED_TRACK;
        return handled(this.activation.firstAction(
          stringValue(properties, "userId")!,
          stringValue(properties, "actionName")!,
          numberValue(properties, "timeSinceSignup")!,
          omitProperties(properties, ["userId", "actionName", "timeSinceSignup"])
        ));
      }
      default:
        if (strict) return handled(this.activation.event(eventName, properties));
        return UNHANDLED_TRACK;
    }
  }

  private dispatchFeatureTrack(
    eventName: string,
    properties: FlowGridTrackProperties,
    strict: boolean
  ): TrackDispatchResult {

    const actions: Record<string, UnifiedFeatureAction> = {
      feature_viewed: "viewed",
      feature_used: "used",
      feature_completed: "completed",
      feature_abandoned: "abandoned",
    };

    const action = actions[eventName] ?? (eventName === "feature_usage" 
    ? properties.action : undefined);

    if (eventName === "feature_usage" || action) {
      if (!this.requireTrackProperties(eventName, properties, ["featureId", "featureName"], strict)) return UNHANDLED_TRACK;
      return handled(this.features.client.trackUsage({
        ...properties,
        action: typeof action === "string" ? action : "used",
        // Emit the lifecycle-specific event name on the wire (feature_viewed,
        // feature_completed, …). For the generic `feature_usage` entry point
        // this is just "feature_usage", preserving the prior behaviour.
        eventName,
      } as Parameters<TrackFeature["trackUsage"]>[0]));
    }

    if (strict) {
      if (!this.requireTrackProperties(eventName, properties, ["userId", "featureName"], true)) return UNHANDLED_TRACK;
      return handled(this.features.create(
        stringValue(properties, "userId")!,
        eventName,
        stringValue(properties, "featureName")!,
        omitProperties(properties, ["userId", "featureName"])
      ));
    }

    return UNHANDLED_TRACK;
  }

  private dispatchPromptTrack(
    eventName: string,
    properties: FlowGridTrackProperties,
    strict: boolean
  ): TrackDispatchResult {
    switch (eventName) {
      case "prompt_submitted":
        if (!this.requireTrackProperties(eventName, properties, ["promptId", "promptType"], strict)) return UNHANDLED_TRACK;
        return handled(this.prompts.client.trackSubmission(properties as Parameters<TrackPrompt["trackSubmission"]>[0]));
      case "prompt_response":
        if (!this.requireTrackProperties(eventName, properties, ["promptId", "responseTime", "completed"], strict)) return UNHANDLED_TRACK;
        return handled(this.prompts.client.trackResponse(properties as Parameters<TrackPrompt["trackResponse"]>[0]));
      case "prompt_feedback":
        if (!this.requireTrackProperties(eventName, properties, ["promptId", "feedbackType"], strict)) return UNHANDLED_TRACK;
        return handled(this.prompts.client.trackFeedback(properties as Parameters<TrackPrompt["trackFeedback"]>[0]));
      case "prompt_conversation":
        if (!this.requireTrackProperties(eventName, properties, ["conversationId", "userId", "turnCount", "totalDuration"], strict)) return UNHANDLED_TRACK;
        return handled(this.prompts.conversation(
          stringValue(properties, "conversationId")!,
          stringValue(properties, "userId")!,
          numberValue(properties, "turnCount")!,
          numberValue(properties, "totalDuration")!
        ));
      default:
        if (strict || eventName.startsWith("prompt_")) return handled(this.prompts.event(eventName, properties));
        return UNHANDLED_TRACK;
    }
  }

  private dispatchAnalyticsTrack(
    eventName: string,
    properties: FlowGridTrackProperties,
    strict: boolean,
    domain?: FlowGridTrackDomain
  ): TrackDispatchResult {
    switch (eventName) {
      case "click_event":
        if (!this.requireTrackProperties(eventName, properties, ["elementId", "elementText"], strict)) return UNHANDLED_TRACK;
        return handled(this.events.client.trackClick(properties as Parameters<Events["trackClick"]>[0]));
      case "form_event":
        if (!this.requireTrackProperties(eventName, properties, ["formId", "formName"], strict)) return UNHANDLED_TRACK;
        return handled(this.events.client.trackForm(properties as Parameters<Events["trackForm"]>[0]));
      case "search_event":
        if (!this.requireTrackProperties(eventName, properties, ["query", "category"], strict)) return UNHANDLED_TRACK;
        return handled(this.events.client.trackSearch(properties as Parameters<Events["trackSearch"]>[0]));
      // `site_search` was the ecommerce module's name for the same thing. It
      // now lands as a `search_event`, which is what the dashboard's site-search
      // views read — so 1.x callers keep feeding them after the ecommerce removal.
      case "site_search":
        if (!this.requireTrackProperties(eventName, properties, ["query"], strict)) return UNHANDLED_TRACK;
        return handled(this.events.client.trackSearch(properties as Parameters<Events["trackSearch"]>[0]));
      case "error_event":
        if (!this.requireTrackProperties(eventName, properties, ["message", "type"], strict)) return UNHANDLED_TRACK;
        return handled(this.events.client.trackError(properties as Parameters<Events["trackError"]>[0]));
      case "funnel_step":
        if (!this.requireTrackProperties(eventName, properties, ["funnelId", "step", "stepNumber"], strict)) return UNHANDLED_TRACK;
        return handled(this.funnels.client.trackStep(properties as Parameters<Funnels["trackStep"]>[0]));
      case "funnel_complete":
        if (!this.requireTrackProperties(eventName, properties, ["funnelId"], strict)) return UNHANDLED_TRACK;
        return handled(this.funnels.complete(stringValue(properties, "funnelId")!, stringValue(properties, "userId"), omitProperties(properties, ["funnelId", "userId"])));
      case "funnel_abandon":
        if (!this.requireTrackProperties(eventName, properties, ["funnelId", "lastStep"], strict)) return UNHANDLED_TRACK;
        return handled(this.funnels.abandon(stringValue(properties, "funnelId")!, stringValue(properties, "lastStep")!, stringValue(properties, "userId"), stringValue(properties, "reason")));
      case "user_activity":
        if (domain && domain !== "retention") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId"], strict)) return UNHANDLED_TRACK;
        return handled(this.retention.client.trackActivity(properties as Parameters<Retention["trackActivity"]>[0]));
      case "attribution_touchpoint":
        if (!this.requireTrackProperties(eventName, properties, ["userId", "channel"], strict)) return UNHANDLED_TRACK;
        return handled(this.attribution.client.trackTouchpoint(properties as Parameters<Attribution["trackTouchpoint"]>[0]));
      case "attribution_conversion":
        if (!this.requireTrackProperties(eventName, properties, ["userId", "conversionType"], strict)) return UNHANDLED_TRACK;
        return handled(this.attribution.client.recordConversion(properties as Parameters<Attribution["recordConversion"]>[0]));
      default:
        return UNHANDLED_TRACK;
    }
  }

  /**
   * Subscription lifecycle — the only part of the old ecommerce dispatcher that
   * survived SDK 2.0. Every removed ecommerce name (add_to_cart, purchase, …)
   * now falls through to `events.track()` as a plain custom event instead of
   * throwing, so 1.x call sites degrade rather than break.
   */
  private dispatchSubscriptionTrack(
    eventName: string,
    properties: FlowGridTrackProperties,
    strict: boolean
  ): TrackDispatchResult {
    switch (eventName) {
      case "subscription_start":
        if (!this.requireTrackProperties(eventName, properties, ["subscriptionId", "userId", "plan", "mrr", "billingCycle"], strict)) return UNHANDLED_TRACK;
        return handled(this.subscriptions.client.start(properties as unknown as Parameters<Subscriptions["start"]>[0]));
      case "subscription_change":
        if (!this.requireTrackProperties(eventName, properties, ["subscriptionId", "userId", "previousPlan", "newPlan", "previousMrr", "newMrr", "changeType"], strict)) return UNHANDLED_TRACK;
        return handled(this.subscriptions.client.change(properties as unknown as Parameters<Subscriptions["change"]>[0]));
      case "subscription_cancel":
        if (!this.requireTrackProperties(eventName, properties, ["subscriptionId", "userId", "plan", "lostMrr"], strict)) return UNHANDLED_TRACK;
        return handled(this.subscriptions.client.cancel(properties as unknown as Parameters<Subscriptions["cancel"]>[0]));
      case "subscription_renew":
        if (!this.requireTrackProperties(eventName, properties, ["subscriptionId", "userId", "mrr"], strict)) return UNHANDLED_TRACK;
        return handled(this.subscriptions.client.renew(stringValue(properties, "subscriptionId")!, stringValue(properties, "userId")!, recordValue(properties, "mrr") as unknown as Parameters<Subscriptions["renew"]>[2]));
      case "trial_converted":
        if (!this.requireTrackProperties(eventName, properties, ["subscriptionId", "userId", "plan", "mrr"], strict)) return UNHANDLED_TRACK;
        return handled(this.subscriptions.client.trialConverted(stringValue(properties, "subscriptionId")!, stringValue(properties, "userId")!, stringValue(properties, "plan")!, recordValue(properties, "mrr") as unknown as Parameters<Subscriptions["trialConverted"]>[3]));
      case "trial_expired":
        if (!this.requireTrackProperties(eventName, properties, ["subscriptionId", "userId"], strict)) return UNHANDLED_TRACK;
        return handled(this.subscriptions.client.trialExpired(stringValue(properties, "subscriptionId")!, stringValue(properties, "userId")!));
      default:
        return UNHANDLED_TRACK;
    }
  }

  private dispatchEnterpriseTrack(
    eventName: string,
    properties: FlowGridTrackProperties,
    strict: boolean,
    domain?: FlowGridTrackDomain
  ): TrackDispatchResult {
    // Enterprise chunk not yet loaded — let the event fall through to events.track().
    if (!_enterpriseFeatures || !_enterpriseTracking) return UNHANDLED_TRACK;

    switch (eventName) {

      case "engagement.dwell_time":
      case "engagement_dwell_time":
        if (domain && domain !== "engagement") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["featureId", "dwellTimeMs"], strict)) return UNHANDLED_TRACK;
        return handled(this.engagement.client.trackDwellTime(properties as Parameters<Engagement["trackDwellTime"]>[0]));

      case "engagement.session_depth":
      case "engagement_session_depth":
        if (domain && domain !== "engagement") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["sessionId", "pagesVisited", "featuresUsed", "actionsTaken", "durationMs"], strict)) return UNHANDLED_TRACK;
        return handled(this.engagement.client.trackSessionDepth(properties as Parameters<Engagement["trackSessionDepth"]>[0]));

      case "engagement.content_consumption":
      case "engagement_content_consumption":
        if (domain && domain !== "engagement") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["contentId", "contentType", "percentageConsumed", "timeSpentMs"], strict)) return UNHANDLED_TRACK;
        return handled(this.engagement.client.trackContentConsumption(properties as Parameters<Engagement["trackContentConsumption"]>[0]));

      case "engagement.user_activity":
      case "engagement_user_activity":
        if (domain && domain !== "engagement") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "activityType", "durationMs"], strict)) return UNHANDLED_TRACK;
        return handled(this.engagement.client.trackUserActivity(properties as unknown as Parameters<Engagement["trackUserActivity"]>[0]));

      case "churn.usage_decay":
      case "churn_usage_decay":
        if (domain && domain !== "churn") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "metric", "periods"], strict)) return UNHANDLED_TRACK;
        return handled(this.churn.client.trackUsageDecay(properties as Parameters<ChurnAnalytics["trackUsageDecay"]>[0]));

      case "churn.nps":
      case "churn_nps":
        if (domain && domain !== "churn") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "score"], strict)) return UNHANDLED_TRACK;
        return handled(this.churn.client.trackNPS(properties as Parameters<ChurnAnalytics["trackNPS"]>[0]));

      case "churn.churn_event":
      case "churn_churn_event":
        if (domain && domain !== "churn") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "churnType"], strict)) return UNHANDLED_TRACK;
        return handled(this.churn.client.trackChurn(properties as Parameters<ChurnAnalytics["trackChurn"]>[0]));

      case "churn.prevention_action":
      case "churn_prevention_action":
        if (domain && domain !== "churn") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "actionType"], strict)) return UNHANDLED_TRACK;
        return handled(this.churn.client.trackPreventionAction(properties as Parameters<ChurnAnalytics["trackPreventionAction"]>[0]));

      case "monetization.revenue":
      case "monetization_revenue":
        if (domain && domain !== "monetization") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "amount", "currency", "type"], strict)) return UNHANDLED_TRACK;
        return handled(this.monetization.client.trackRevenue(properties as Parameters<Monetization["trackRevenue"]>[0]));

      case "support.ticket_created":
      case "support_ticket_created":
        if (domain && domain !== "support") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["ticketId", "userId", "subject", "category", "priority", "channel"], strict)) return UNHANDLED_TRACK;
        return handled(this.support.client.trackTicket(properties as Parameters<SupportAnalytics["trackTicket"]>[0]));

      case "support.ticket_updated":
      case "support_ticket_updated":
        if (domain && domain !== "support") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["ticketId", "status"], strict)) return UNHANDLED_TRACK;
        return handled(this.support.client.updateTicket(properties as Parameters<SupportAnalytics["updateTicket"]>[0]));

      case "support.csat":
      case "support_csat":
        if (domain && domain !== "support") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["ticketId", "userId", "score"], strict)) return UNHANDLED_TRACK;
        return handled(this.support.client.trackCSAT(properties as Parameters<SupportAnalytics["trackCSAT"]>[0]));

      case "support.feature_complaint":
      case "support_feature_complaint":
        if (domain && domain !== "support") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "featureId", "complaintType", "severity"], strict)) return UNHANDLED_TRACK;
        return handled(this.support.client.trackFeatureComplaint(properties as Parameters<SupportAnalytics["trackFeatureComplaint"]>[0]));

      case "acquisition.acquired":
      case "acquisition_acquired":
        if (domain && domain !== "acquisition") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "source"], strict)) return UNHANDLED_TRACK;
        return handled(this.acquisition.client.trackAcquisition(properties as Parameters<AcquisitionAnalytics["trackAcquisition"]>[0]));

      case "acquisition.lifecycle_transition":
      case "acquisition_lifecycle_transition":
        if (domain && domain !== "acquisition") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "fromStage", "toStage"], strict)) return UNHANDLED_TRACK;
        return handled(this.acquisition.client.trackLifecycleTransition(properties as Parameters<AcquisitionAnalytics["trackLifecycleTransition"]>[0]));

      case "acquisition.campaign_spend":
      case "acquisition_campaign_spend":
        if (domain && domain !== "acquisition") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["campaignId", "amount", "date"], strict)) return UNHANDLED_TRACK;
        return handled(this.acquisition.client.trackCampaignSpend(properties as unknown as Parameters<AcquisitionAnalytics["trackCampaignSpend"]>[0]));

      case "paths.transition":
      case "paths_transition":
        if (domain && domain !== "paths") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "fromScreen", "toScreen"], strict)) return UNHANDLED_TRACK;
        return handled(this.paths.client.trackTransition(properties as Parameters<PathAnalytics["trackTransition"]>[0]));

      case "paths.feature_sequence":
      case "paths_feature_sequence":
        if (domain && domain !== "paths") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "feature", "order"], strict)) return UNHANDLED_TRACK;
        return handled(this.paths.client.trackFeatureSequence(properties as unknown as Parameters<PathAnalytics["trackFeatureSequence"]>[0]));

      case "security.login_attempt":
      case "security_login_attempt":
        if (domain && domain !== "security") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "success"], strict)) return UNHANDLED_TRACK;
        return handled(this.security.client.trackLoginAttempt(properties as Parameters<SecurityAnalytics["trackLoginAttempt"]>[0]));

      case "security.role_change":
      case "security_role_change":
        if (domain && domain !== "security") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "changedBy", "previousRole", "newRole"], strict)) return UNHANDLED_TRACK;
        return handled(this.security.client.trackRoleChange(properties as Parameters<SecurityAnalytics["trackRoleChange"]>[0]));

      case "security.permission_change":
      case "security_permission_change":
        if (domain && domain !== "security") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "changedBy", "permission", "action"], strict)) return UNHANDLED_TRACK;
        return handled(this.security.client.trackPermissionChange(properties as Parameters<SecurityAnalytics["trackPermissionChange"]>[0]));

      case "security.data_access":
      case "security_data_access":
        if (domain && domain !== "security") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "resourceType", "accessType"], strict)) return UNHANDLED_TRACK;
        return handled(this.security.client.trackDataAccess(properties as Parameters<SecurityAnalytics["trackDataAccess"]>[0]));
        
      case "security.compliance_event":
      case "security_compliance_event":
        if (domain && domain !== "security") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["userId", "eventType"], strict)) return UNHANDLED_TRACK;
        return handled(this.security.client.trackComplianceEvent(properties as Parameters<SecurityAnalytics["trackComplianceEvent"]>[0]));

      case "multi_path_funnels.track_step":
      case "multi_path_funnels_track_step":
        if (domain && domain !== "multiPathFunnels") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["funnelId", "step", "userId"], strict)) return UNHANDLED_TRACK;
        return handled(this.multiPathFunnels.client.trackStep(properties as Parameters<MultiPathFunnels["trackStep"]>[0]));

      case "multi_path_funnels.track_micro_conversion":
      case "multi_path_funnels_track_micro_conversion":
        if (domain && domain !== "multiPathFunnels") return UNHANDLED_TRACK;
        if (!this.requireTrackProperties(eventName, properties, ["conversionId", "userId"], strict)) return UNHANDLED_TRACK;
        return handled(this.multiPathFunnels.client.trackMicroConversion(properties as Parameters<MultiPathFunnels["trackMicroConversion"]>[0]));

      default:
        return UNHANDLED_TRACK;
        
    }
  }

  /**
   * Identify a user: identity only, with no sign-in event and no active-user
   * ping. This is the lower-level call; prefer {@link setUser} on every page
   * load (it calls this once per user) or {@link trackAuth} at sign-in. Traits
   * passed here feed feature-flag property rules for this page load only.
   *
   * @example
   * ```ts
   * fg.identifyUser("u_1", { plan: "pro", email: "alice@example.com" });
   * ```
   */
  identifyUser(userId: string, traits?: Record<string, unknown>, anonymousId?: string) {
    this._identifiedUserId = userId;

    // Feature flags need to know who this is: a rule that targets a user, or a
    // percentage rollout keyed on identity, cannot be right until now. The
    // engine re-evaluates and re-asks the server for anything server-only, then
    // notifies subscribers so the UI re-renders with the correct answer.
    try {
      this.flags.identify({
        userId,
        ...(traits ? { attributes: flattenTraits(traits) } : {}),
      });
    } catch {
      /* flags are never allowed to break identify */
    }

    return this.identify.user(userId, traits, anonymousId);
  }

  /**
   * Bind a feature's `id` + `name` (and optional `category`) once, and get back
   * a small set of semantic helpers — `viewed` / `used` / `completed` /
   * `abandoned` — so you don't repeat the id/name on every call. Each helper is
   * a thin, explicit wrapper over `track("feature_usage", …)` (full
   * routing/consent/transport). No hidden state, no lifecycle guessing: every
   * call sends exactly the action you name. The dashboard does the interpreting
   * (a *viewed* feature is Discovered, a *used* one is Adopted).
   *
   * @example
   * ```ts
   * const agent = fg.defineFeature("ai-agent-builder", "AI Agent Builder", { category: "automation" });
   * agent.viewed();                 // → action: "viewed"  (Discovered + Viewed)
   * agent.used({ model: "opus" });  // → action: "used"    (Adopted)
   * agent.completed();              // → action: "completed"
   * agent.abandoned();              // → action: "abandoned"
   * ```
   */
  defineFeature(
    featureId: string,
    featureName: string,
    opts: { category?: string } = {},
  ): FeatureHandle {
    const emit = (action: FeatureHandleAction, properties?: Record<string, unknown>) =>
      this.track(
        "feature_usage",
        {
          featureId,
          featureName,
          action,
          ...(opts.category ? { category: opts.category } : {}),
          ...(properties ?? {}),
        },
        { domain: "feature" },
      );
    return {
      featureId,
      featureName,
      viewed: (properties) => emit("viewed", properties),
      used: (properties) => emit("used", properties),
      completed: (properties) => emit("completed", properties),
      abandoned: (properties) => emit("abandoned", properties),
      track: (action, properties) => emit(action, properties),
    };
  }

  // NOTE (Knowledge Base): deliberately NOT exposed on the browser SDK. The
  // knowledge corpus is server-side LLM context — private by design — and a
  // browser method would ship it to anyone who can read the page's API key.
  // Use `FlowGridServer.getDocs/searchDocs/knowledgeManifest` from
  // `flowgrid-sdk/server` instead; the core API also rejects browser-origin
  // requests outright. Do not add browser knowledge methods back.

  /**
   * Capture one of your own events, stored under exactly this name. For the
   * micro-interactions `track()`'s named events don't cover — a filter
   * applied, a tab opened, a tooltip read. Fire and forget: never throws.
   *
   * @example
   * ```ts
   * fg.capture("filter_applied", { filter: "price", value: "under-50" });
   * ```
   */
  capture(name: string, properties?: Record<string, unknown>): Promise<void> {
    return this.events.capture(name, properties);
  }

  /**
   * Track a page view. Auto-detects URL/path/title/referrer in the browser.
   *
   * @example
   * ```ts
   * fg.page();                            // auto-detect everything
   * fg.page("/pricing", { plan: "pro" }); // override path + properties
   * ```
   */
  page(
    pathOrUrl?: string,
    properties?: Record<string, unknown>,
    options?: { title?: string; referrer?: string; userId?: string; sessionId?: string }
  ) {
    return this.pageViews.view(pathOrUrl, properties, options);
  }

  /**
   * Enable browser-side auto-instrumentation in a single call.
   *
   * On by default (opt-out with `false`):
   * - **Engagement** — session depth, action count, and active/passive/idle transitions
   * - **Replay** — DOM session recording via rrweb
   *
   * Off by default (these are handled by the flowgrid.js core script):
   * - **Sessions**, **Page views**, **Performance**, **Heatmaps**, **Attribution**
   *
   * Pass `false` for any feature to disable it. Returns a single teardown
   * function that detaches every listener; safe no-op on the server.
   *
   * @example
   * ```ts
   * const stop = fg.autoTrack({ userId: "u_1" });
   * // ...later
   * stop();
   * ```
   */
  autoTrack(options: {
    userId?: string;
    sessions?: boolean;
    pageViews?: boolean;
    performance?: boolean | { resources?: boolean; minDuration?: number };
    heatmaps?: boolean | { movement?: boolean };
    attribution?: boolean;
    replay?: boolean | import("./features/analytics/replay").SessionReplayOptions;
    engagement?: boolean | { idleAfterMs?: number; pollMs?: number };
    passiveIdentity?: boolean | import("./features/analytics/identify").PassiveIdentityOptions;
  } = {}): () => void {
    const teardowns: Array<() => void> = [];
    const ctx = { userId: options.userId };

    if (options.sessions === true) {
      teardowns.push(this.sessions.autoTrack(ctx.userId ? { user: { userId: ctx.userId } } : {}));
    }
    if (options.pageViews === true) {
      teardowns.push(this.pageViews.autoTrack(ctx));
    }
    if (options.performance === true || (options.performance !== false && typeof options.performance === "object")) {
      const perfOpts = typeof options.performance === "object" ? options.performance : {};
      teardowns.push(this.performance.autoTrack({ ...perfOpts, userId: ctx.userId }));
    }
    if (options.heatmaps === true || (options.heatmaps !== false && typeof options.heatmaps === "object")) {
      const hmOpts = typeof options.heatmaps === "object" ? options.heatmaps : {};
      teardowns.push(this.heatmaps.autoTrack({ ...hmOpts, userId: ctx.userId }));
    }
    if (options.engagement !== false) {
      const engOpts = typeof options.engagement === "object" ? options.engagement : {};
      // Engagement lives in the enterprise chunk — start async (mirrors replay pattern).
      let resolvedEngStop: (() => void) | null = null;
      let engStopRequested = false;
      void loadEnterpriseModules().then(() => {
        if (engStopRequested) return;
        resolvedEngStop = this.engagement.client.autoTrack({ ...engOpts, userId: ctx.userId });
      });
      teardowns.push(() => {
        engStopRequested = true;
        if (resolvedEngStop) try { resolvedEngStop(); } catch { /* noop */ }
      });
    }
    if (options.attribution === true && ctx.userId) {
      this.attribution.captureFromUrl(ctx.userId);
    }
    // Passive identity capture is off by default — the flowgrid.js core script
    // already runs it for snippet installs; SDK-only sites opt in here.
    if (options.passiveIdentity === true || (options.passiveIdentity !== false && typeof options.passiveIdentity === "object")) {
      const piOpts = typeof options.passiveIdentity === "object" ? options.passiveIdentity : {};
      teardowns.push(this.identify.client.autoTrackForms(piOpts));
    }
    if (options.replay !== false) {
      const replayOpts = typeof options.replay === "object" ? options.replay : {};
      // start() is async; track its resolved teardown so the outer stop()
      // tears down recording even if the user calls it before rrweb resolved.
      let resolvedStop: (() => void) | null = null;
      let stopRequested = false;
      void this.replay.start({ userId: ctx.userId, ...replayOpts }).then(stop => {
        if (stopRequested) { try { stop(); } catch { /* noop */ } }
        else { resolvedStop = stop; }
      });
      teardowns.push(() => {
        stopRequested = true;
        if (resolvedStop) { try { resolvedStop(); } catch { /* noop */ } }
      });
    }

    return () => { for (const t of teardowns) { try { t(); } catch { /* swallow */ } } };
  }
}

export default FlowGrid;

// Correct brand casing is "Flowgrid" (capital F, lowercase g) — "FlowGrid" was
// a naming mistake this SDK shipped with. `FlowGrid` stays exported so no
// existing `import { FlowGrid } from "flowgrid-sdk"` breaks; `Flowgrid` is the
// canonical name every new binding/doc in this SDK uses.
export { FlowGrid as Flowgrid };
